import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { MonthlyPoint } from '../../api/types';

interface Props {
  points: MonthlyPoint[];
  loading?: boolean;
  onMonthClick?: (month: string) => void;
}

const ADOBE_COLOR = '#dc2626';     // red 600 (Adobe brand red)
const NON_ADOBE_COLOR = '#71717a'; // zinc 500

function formatUsd(n: number): string {
  if (n >= 10000) return `$${(n / 1000).toFixed(1)}k`;
  if (n >= 1000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (n >= 1) return `$${n.toFixed(0)}`;
  return `$${n.toFixed(2)}`;
}

export function MonthlyCostByOriginChart({ points, loading, onMonthClick }: Props): JSX.Element {
  const months = points.map((p) => p.month);

  const option: EChartsOption = {
    grid: { left: 64, right: 24, top: 56, bottom: 36 },
    legend: {
      top: 8,
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 12 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      valueFormatter: (raw) => formatUsd(Number(raw ?? 0)),
    },
    xAxis: {
      type: 'category',
      data: months,
    },
    yAxis: {
      type: 'value',
      name: 'Cost (USD)',
      nameLocation: 'middle',
      nameGap: 52,
      axisLabel: { formatter: (v: number) => formatUsd(v) },
    },
    series: [
      {
        name: 'Adobe',
        type: 'bar',
        stack: 'origin',
        barMaxWidth: 40,
        itemStyle: { color: ADOBE_COLOR },
        data: points.map((p) => Number(p.costByOrigin.adobe.toFixed(2))),
      },
      {
        name: 'Non-Adobe',
        type: 'bar',
        stack: 'origin',
        barMaxWidth: 40,
        itemStyle: { color: NON_ADOBE_COLOR },
        data: points.map((p) => Number(p.costByOrigin.nonAdobe.toFixed(2))),
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 320, width: '100%', cursor: onMonthClick ? 'pointer' : 'default' }}
      showLoading={loading}
      notMerge
      lazyUpdate
      onEvents={
        onMonthClick
          ? {
              click: (params: { name?: string }) => {
                if (params?.name) onMonthClick(params.name);
              },
            }
          : undefined
      }
    />
  );
}
